import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const AdminNotifications = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications();
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const getCategoryColor = (category) => {
    const colors = {
      'appointment': '#3b82f6',
      'estimate': '#10b981',
      'message': '#f59e0b',
      'signature': '#ff9800',
      'system': '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'appointment': '📅',
      'estimate': '💰',
      'message': '💬',
      'signature': '✍️',
      'system': '🔔'
    };
    return icons[category] || '🔔';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': '#ef4444',
      'medium': '#f59e0b',
      'low': '#10b981'
    };
    return colors[priority] || '#6b7280';
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesFilter = filter === 'all' || 
      (filter === 'unread' && !notification.read) ||
      (filter === 'read' && notification.read);
    
    const matchesCategory = categoryFilter === 'all' || 
      notification.category === categoryFilter;
    
    return matchesFilter && matchesCategory;
  });

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="modern-loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="analytics-container animate-fade-in-up" style={{ padding: '2rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '600', color: '#1f2937' }}>
            Admin Notifications
          </h1>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            Manage and view all system notifications
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem', 
          marginBottom: '2rem' 
        }}>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem'
              }}>
                🔔
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {notifications.length}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Total Notifications
                </div>
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem'
              }}>
                ⚠️
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {unreadCount}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Unread Notifications
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                Filter by Status:
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">All Notifications</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                Filter by Category:
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">All Categories</option>
                <option value="appointment">Appointments</option>
                <option value="estimate">Estimates</option>
                <option value="message">Messages</option>
                <option value="signature">Signatures</option>
                <option value="system">System</option>
              </select>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '1.5rem'
                }}
              >
                Mark All as Read
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          {filteredNotifications.length > 0 ? (
            <div>
              {filteredNotifications.map((notification, index) => (
                <div
                  key={notification.id}
                  style={{
                    padding: '1.5rem',
                    borderBottom: index < filteredNotifications.length - 1 ? '1px solid #f3f4f6' : 'none',
                    backgroundColor: notification.read ? '#ffffff' : '#f8fafc',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = notification.read ? '#f9fafb' : '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = notification.read ? '#ffffff' : '#f8fafc';
                  }}
                  onClick={() => handleMarkAsRead(notification.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: getCategoryColor(notification.category),
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      flexShrink: 0
                    }}>
                      {getCategoryIcon(notification.category)}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <h3 style={{ 
                          margin: 0, 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          color: '#1f2937',
                          flex: 1
                        }}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444',
                            flexShrink: 0
                          }} />
                        )}
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: getPriorityColor(notification.priority),
                          color: 'white'
                        }}>
                          {notification.priority?.toUpperCase() || 'MEDIUM'}
                        </span>
                      </div>

                      <p style={{ 
                        margin: '0 0 0.5rem 0', 
                        color: '#6b7280', 
                        fontSize: '0.875rem',
                        lineHeight: '1.5'
                      }}>
                        {notification.message}
                      </p>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                        <span>
                          {notification.createdAt instanceof Date 
                            ? notification.createdAt.toLocaleString() 
                            : new Date(notification.createdAt).toLocaleString()
                          }
                        </span>
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '8px',
                          backgroundColor: getCategoryColor(notification.category) + '20',
                          color: getCategoryColor(notification.category),
                          fontWeight: '500'
                        }}>
                          {notification.category?.toUpperCase() || 'SYSTEM'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🔔</div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1.125rem', fontWeight: '600' }}>
                No notifications found
              </h3>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {filter === 'unread' ? 'No unread notifications' : 'No notifications match your current filters'}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminNotifications;
